import { useState, useEffect } from 'react';
import { Save, Loader2, RefreshCw, Eye, EyeOff, AlertTriangle, CheckCircle, XCircle, CreditCard, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const StripeManager = () => {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        adminApi.getStripeSettings(),
        adminApi.getStripeStatus(),
      ]);
      setSettings(settingsRes.data);
      setStatus(statusRes.data);
      setIsLiveMode(settingsRes.data?.is_live_mode || false);
    } catch (error) {
      console.error('Error fetching Stripe data:', error);
      toast.error('Failed to load Stripe settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newApiKey) {
      toast.error('Please enter a Stripe API key');
      return;
    }

    if (!newApiKey.startsWith('sk_test_') && !newApiKey.startsWith('sk_live_')) {
      toast.error('Invalid key format. Key should start with sk_test_ or sk_live_');
      return;
    }

    setSaving(true);
    try {
      await adminApi.updateStripeSettings({
        api_key: newApiKey,
        is_live_mode: isLiveMode,
        webhook_secret: webhookSecret || null,
      });
      toast.success('Stripe settings updated successfully');
      setNewApiKey('');
      setWebhookSecret('');
      fetchData();
    } catch (error) {
      console.error('Error saving Stripe settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await adminApi.testStripeConnection();
      if (response.data.success) {
        toast.success(`Connection successful! Using ${response.data.key_type} key`);
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default settings? This will use the environment variable key.')) {
      return;
    }
    
    setResetting(true);
    try {
      await adminApi.resetStripeSettings();
      toast.success('Settings reset to default');
      fetchData();
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast.error('Failed to reset settings');
    } finally {
      setResetting(false);
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;
    
    if (!status.is_configured) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-full text-sm">
          <XCircle className="w-4 h-4" />
          Not Configured
        </div>
      );
    }
    
    if (status.key_type === 'live') {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-full text-sm">
          <CheckCircle className="w-4 h-4" />
          Live Mode
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
        <AlertTriangle className="w-4 h-4" />
        Test Mode
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="stripe-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Stripe Settings</h1>
          <p className="text-muted-foreground">Manage your Stripe payment integration.</p>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge()}
        </div>
      </div>

      {/* Current Configuration */}
      <div className="admin-card mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold">Current Configuration</h2>
            <p className="text-muted-foreground text-sm">Active Stripe API settings</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Key Source</p>
              <p className="font-medium capitalize">{settings?.key_source || 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Current Key</p>
              <div className="flex items-center gap-2">
                <code className="bg-white/5 px-3 py-1.5 rounded font-mono text-sm">
                  {showKey ? (settings?.masked_key || 'Not configured') : '••••••••••••••••'}
                </code>
                <button 
                  onClick={() => setShowKey(!showKey)}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Mode</p>
              <p className="font-medium">{status?.is_live_mode ? 'Live' : 'Test'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Last Updated</p>
              <p className="font-medium">
                {settings?.updated_at 
                  ? new Date(settings.updated_at).toLocaleDateString('en-US', { 
                      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                    })
                  : 'Never'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-6 border-t border-white/10">
          <Button
            onClick={handleTest}
            disabled={testing}
            variant="outline"
            className="btn-outline"
            data-testid="test-connection-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Test Connection
          </Button>
          {settings?.has_custom_key && (
            <Button
              onClick={handleReset}
              disabled={resetting}
              variant="ghost"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="reset-settings-btn"
            >
              {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Reset to Default
            </Button>
          )}
        </div>
      </div>

      {/* Update API Key */}
      <div className="admin-card">
        <h2 className="text-xl font-heading font-semibold mb-6">Update API Key</h2>
        
        <div className="space-y-6">
          <div className="form-group">
            <label htmlFor="api_key" className="form-label">Stripe Secret Key</label>
            <Input
              id="api_key"
              type="password"
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder="sk_test_... or sk_live_..."
              className="input-neon font-mono"
              data-testid="stripe-api-key-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Find your API keys in your{' '}
              <a 
                href="https://dashboard.stripe.com/apikeys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Stripe Dashboard → Developers → API keys
              </a>
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="webhook_secret" className="form-label">Webhook Secret (Optional)</label>
            <Input
              id="webhook_secret"
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder="whsec_..."
              className="input-neon font-mono"
              data-testid="stripe-webhook-secret-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Optional: Used to verify webhook signatures
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
            <div>
              <p className="font-medium">Live Mode</p>
              <p className="text-sm text-muted-foreground">
                Enable this only when you're ready to accept real payments
              </p>
            </div>
            <Switch
              checked={isLiveMode}
              onCheckedChange={setIsLiveMode}
              data-testid="stripe-live-mode-switch"
            />
          </div>

          {isLiveMode && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-400">Live Mode Warning</p>
                  <p className="text-sm text-red-300/80">
                    You're about to enable live mode. Real charges will be made to customers' cards. 
                    Make sure you've tested thoroughly in test mode first.
                  </p>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving || !newApiKey}
            className="btn-primary"
            data-testid="save-stripe-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save API Key
          </Button>
        </div>
      </div>

      {/* Info Section */}
      <div className="admin-card mt-6 bg-primary/5 border-primary/20">
        <h3 className="font-heading font-semibold mb-4">About Stripe Integration</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• <strong>Test Mode:</strong> Use keys starting with <code className="bg-white/10 px-1 rounded">sk_test_</code> for development</li>
          <li>• <strong>Live Mode:</strong> Use keys starting with <code className="bg-white/10 px-1 rounded">sk_live_</code> for production</li>
          <li>• Your API key is encrypted and stored securely in the database</li>
          <li>• Resetting will revert to the default key from environment variables</li>
        </ul>
      </div>
    </div>
  );
};

export default StripeManager;
